import * as React from "react"
import styles from "./checkout.css?inline"
import { BookingDisclosure } from "./components/BookingDisclosure"
import { BookingLoading } from "./components/BookingLoading"
import { BookingSidebar } from "./components/BookingSidebar"
import { BookingSummary } from "./components/BookingSummary"
import { getBookingSummary } from "./components/BookingSummaryContent"
import { CheckoutTrust } from "./components/CheckoutTrust"
import { Icon } from "./components/Icon"
import { HireDateFields } from "./components/HireDateFields"
import { HireProcess } from "./components/HireProcess"
import { SelectionFooter } from "./components/SelectionFooter"
import { MobileBookingTotals } from "./components/MobileBookingTotals"
import { MobilePaymentFooter } from "./components/MobilePaymentFooter"
import { Loading, SupportLinks } from "./components/StatusMessages"
import { formatDate, money } from "./format"
import { getCheckoutPresentation } from "./presentation"
import { AddonsStep } from "./steps/AddonsStep"
import { DatesStep } from "./steps/DatesStep"
import { PaymentStep } from "./steps/PaymentStep"
import type { CheckoutController } from "./useCheckoutController"
import { confirmationPresentation } from "./confirmationPresentation"

export function CheckoutView({ checkout }: { checkout: CheckoutController }) {
    const view = { ...checkout, ...getCheckoutPresentation(checkout) }
    const {
        instanceId,
        journeyStep,
        bookingPane,
        phase,
        props,
        dateNotice,
        activeSlug,
        meta,
        isMobile,
        product,
        error,
        setProductRetry,
        calendarOpen,
        calendarFeedback,
        choice,
        previewDates,
        postcode,
        normalizedPostcode,
        deliveryContactRequired,
        quantity,
        deliveryFeePence,
        quote,
        editBooking,
        selectedAddons,
        bookingStatus,
        setCalendarOpen,
        confirmDateSelection,
        range,
        activeDateField,
        openDatePicker,
        resetDatesButton,
        pricePromise,
        calendar,
        dateMessage,
        bookingInputsReady,
        mobileDisabled,
        mobileAction,
        mobileLabel,
        hireAmountPence,
        mobileAmountPence,
        mobileDeliveryPence,
        selectionChecking,
        checkoutPreparing,
        confirmationChecking,
        confirmationError,
        retryConfirmation,
    } = view
    const bookingSummary = product && !deliveryContactRequired ? getBookingSummary({ product, image: meta.image, choice, dates: previewDates, postcode, quantity, deliveryFeePence, quote, selectedAddons }) : null
    const confirmation = bookingStatus ? confirmationPresentation(bookingStatus) : null
    return (
        <div data-arctic-booking={instanceId} className={`ah-root ah-step-${journeyStep} ah-booking-pane-${bookingPane} ah-phase-${phase}`} style={{ "--ah-primary": props.primaryColour, "--ah-surface": props.surfaceColour, "--ah-radius": `${props.borderRadius}px` } as React.CSSProperties}>
            <style>{styles}</style>
            {dateNotice?.target === "calendar" && <div key={dateNotice.id} className="ah-date-notice" role="status" aria-live="polite">{dateNotice.message}</div>}
            <div className="ah-shell" {...(calendarOpen && isMobile ? { inert: "", "aria-hidden": true } : {})}>
                {phase === "loading" && activeSlug && <BookingLoading meta={meta} isMobile={isMobile} quantity={quantity} />}

                {phase !== "loading" && !product && (
                    <section className="ah-panel ah-empty"><h3>Booking is unavailable</h3><p>{error || "The selected product is not configured for online booking."}</p>{activeSlug && <button type="button" className="ah-secondary-button" onClick={() => setProductRetry(value => value + 1)}>Try again</button>}<SupportLinks phone={props.supportPhone} whatsapp={props.supportWhatsApp} /></section>
                )}

                {product && phase !== "loading" && phase !== "confirming" && phase !== "complete" && (
                    <>
                        {!calendarOpen && !(journeyStep === "booking" && bookingPane === "selection") && calendarFeedback}
                        <div className="ah-layout">
                            <BookingSidebar>
                                {!isMobile && <HireProcess />}
                                <BookingSummary summary={bookingSummary} totalLabel={journeyStep === "booking" ? "Hire cost" : undefined} showReassurance={journeyStep === "payment"} onEdit={journeyStep === "payment" ? () => void editBooking() : undefined} />
                                <CheckoutTrust />
                            </BookingSidebar>
                            <main className="ah-main">
                                {journeyStep !== "booking" && <BookingDisclosure summary={bookingSummary} onEdit={() => void editBooking()} />}

                                {journeyStep === "booking" && bookingPane === "selection" && (
                                    <>
                                        {isMobile && <HireProcess />}
                                        <DatesStep {...view} />
                                    </>
                                )}

                                {journeyStep === "booking" && bookingPane === "addons" && (
                                    <AddonsStep {...view} product={product} />
                                )}

                                {journeyStep === "payment" && (
                                    <PaymentStep {...view} product={product} />
                                )}
                            </main>
                        </div>
                    </>
                )}

                {phase === "confirming" && <section className="ah-panel ah-confirming" aria-live="polite">
                    {confirmationChecking ? <><Loading label="Confirming your booking…" /><p>Payment may already be complete. Keep this page open while Stripe confirms it.</p></> : <><h2>We couldn’t check your booking</h2><p>{confirmationError}</p><button type="button" className="ah-secondary-button" onClick={() => void retryConfirmation()}>Check again</button><SupportLinks phone={props.supportPhone} whatsapp={props.supportWhatsApp} /></>}
                </section>}

                {phase === "complete" && bookingStatus && confirmation && (
                    <section className="ah-panel ah-success" aria-live="polite">
                        {confirmation.outcome === "confirmed" && <div className="ah-success-icon"><Icon name="check" /></div>}
                        <div className="ah-eyebrow">{confirmation.heading}</div><h2>{bookingStatus.bookingReference}</h2><p>{confirmation.message}</p>
                        {!confirmation.final && <>{confirmationError && <p role="status">{confirmationError}</p>}{confirmationChecking ? <p>Checking payment status…</p> : <button type="button" className="ah-secondary-button" onClick={() => void retryConfirmation()}>Check again</button>}</>}
                        <dl className="ah-confirmation-grid"><div><dt>Equipment</dt><dd>{bookingStatus.product.name} × {bookingStatus.quantity}</dd></div><div><dt>Delivery</dt><dd>{formatDate(bookingStatus.deliveryDate)}</dd></div><div><dt>Collection</dt><dd>{formatDate(bookingStatus.collectionDate)}</dd></div><div><dt>{confirmation.paymentLabel}</dt><dd>{money(bookingStatus.totalPence)}</dd></div></dl>
                        {confirmation.outcome !== "confirmed" && <p>Please contact <a href="mailto:contact@arctic-hire.co.uk">contact@arctic-hire.co.uk</a> if you need help with your booking.</p>}
                    </section>
                )}
            </div>

            {calendarOpen && isMobile && product && !deliveryContactRequired && (
                <div className="ah-calendar-modal" role="dialog" aria-modal="true" aria-label="Choose delivery and collection dates">
                    <header><button type="button" className="ah-calendar-close" onClick={() => setCalendarOpen(false)} aria-label="Close date picker">×</button>{resetDatesButton}</header>
                    <div className="ah-calendar-modal-fields">
                        <HireDateFields range={range} activeDateField={activeDateField} onSelect={openDatePicker} />
                        {pricePromise}
                    </div>
                    <div className="ah-calendar-scroll">{calendarFeedback}{error && <div className="ah-alert ah-error" role="alert">{error}</div>}{calendar}</div>
                    <footer className="ah-mobile-selection-footer">
                        {choice ? <MobileBookingTotals label="Hire cost" amountPence={hireAmountPence} deliveryPence={mobileDeliveryPence} hireLabel={null} /> : <p className="ah-calendar-selection-hint" role="status">{dateMessage}</p>}
                        <button type="button" className="ah-primary-button ah-calendar-confirm" disabled={!bookingInputsReady} onClick={confirmDateSelection}><span className="ah-continue-content"><Icon name="calendar" /><span>{choice ? `Select ${choice.label}` : "Select hire dates"}</span></span></button>
                    </footer>
                </div>
            )}

            {product && phase !== "loading" && phase !== "confirming" && phase !== "complete" && !calendarOpen && !deliveryContactRequired && (
                journeyStep === "payment" ? (
                    <MobilePaymentFooter amountPence={mobileAmountPence} deliveryPence={mobileDeliveryPence} dates={quote ?? previewDates ?? undefined} busy={checkoutPreparing || phase === "paying"} disabled={mobileDisabled} label={mobileLabel} onPay={() => void mobileAction()} />
                ) : journeyStep === "booking" && bookingPane === "selection" ? (
                    <SelectionFooter amountPence={choice ? mobileAmountPence : undefined} deliveryPence={mobileDeliveryPence} showDelivery={Boolean(normalizedPostcode)} hireLabel={choice?.label} checking={selectionChecking} disabled={mobileDisabled} onContinue={() => void mobileAction()} />
                ) : (
                    <div className="ah-mobile-bar"><div className="ah-mobile-total"><small>Hire cost</small><strong>{money(mobileAmountPence)}</strong>{mobileDeliveryPence !== null && <small className="ah-mobile-delivery">{mobileDeliveryPence === 0 ? <>+ <span className="ah-mobile-delivery-free">FREE delivery</span></> : <>+{money(mobileDeliveryPence)} delivery</>}</small>}</div><button type="button" className="ah-primary-button" disabled={mobileDisabled} aria-busy={selectionChecking} onClick={() => void mobileAction()}>{mobileLabel}</button></div>
                )
            )}
        </div>
    )
}
